import type { ColumnDef } from "@kontextwerk/web-sdk/ui/layout/Table.svelte"
import {
  formatContactAddress,
  parseContactAddress,
} from "../../../../../../../../../../../_shared/_logic/contact-addresses"
import {
  contactPointImportSyncPresentation,
  contactPointOriginPresentation,
} from "../../../../../../../../../../../_shared/_logic/contact-quality-tags"
import { formatAddressLines } from "../../../../../../../../../../../../../_shared/_logic/countries"
import { contactChannelTagsForContactPoint } from "../_shared/_logic/contact-channels"
import type { CustomerContactRow } from "./contract"
import { contactPointKindLabel as sharedContactPointKindLabel } from "../../../../../../../../../../../_shared/_logic/contact-point-kinds"

export const contactColumns: ColumnDef[] = [
  {
    id: "label",
    label: "Label",
    isPrimary: true,
    sortable: true,
    sortKey: "label",
    sortType: "text",
  },
  {
    id: "kindLabel",
    label: "Typ",
    sortable: true,
    sortKey: "kindLabel",
    sortType: "text",
  },
  {
    id: "channelsLabel",
    label: "Kanäle",
    sortable: true,
    sortKey: "channelsLabel",
    sortType: "text",
  },
  {
    id: "value",
    label: "Wert",
    sortable: true,
    sortKey: "value",
    sortType: "text",
  },
  {
    id: "verifiedLabel",
    label: "Verifiziert",
    sortable: true,
    sortKey: "verifiedLabel",
    sortType: "boolean",
    defaultSortDir: "desc",
    getSortValue: (row) => (row as CustomerContactRow).contact.is_verified,
  },
  {
    id: "sourceLabel",
    label: "Herkunft",
    sortable: true,
    sortKey: "sourceLabel",
    sortType: "text",
  },
]

export const contactKindLabel = (kind: string) => {
  const label = sharedContactPointKindLabel(kind)

  return label === "Kontaktpunkt" ? "Kontakt" : label
}

export const contactValueLabel = (entry: ContactPoint) =>
  entry.kind === "ADDRESS" ? formatContactAddress(entry.value) : entry.value

export const contactValueLines = (entry: ContactPoint) => {
  if (entry.kind !== "ADDRESS") return [entry.value]

  const lines = formatAddressLines(parseContactAddress(entry.value))

  return lines.length ? lines : [formatContactAddress(entry.value)]
}

export const contactKindTag = (kind: string) => {
  if (kind === "EMAIL") {
    return {
      label: "E-Mail",
      color: "blue",
      tooltip: "",
    }
  }

  if (kind === "MOBILE") {
    return {
      label: "Mobil",
      color: "teal",
      tooltip: "Für Anrufe und mobile Nachrichten nutzbar.",
    }
  }

  if (kind === "FIXED_LINE") {
    return {
      label: "Festnetz",
      color: "teal",
      tooltip: "Für Anrufe nutzbar.",
    }
  }

  if (kind === "ADDRESS") {
    return {
      label: "Adresse",
      color: "amber",
      tooltip: "",
    }
  }

  return {
    label: "Kontakt",
    color: "rose",
    tooltip: "Kontaktpunkt mit unbekanntem Typ.",
  }
}

export const verificationTag = (entry: ContactPoint) =>
  entry.is_verified
    ? {
        label: "Verifiziert",
        color: "emerald",
        tooltip: "Wird bei der automatischen Kontaktaufnahme bevorzugt.",
      }
    : {
        label: "Nicht verifiziert",
        color: "orange",
        tooltip: "Wird erst nach bereits bestätigten Kontaktwegen genutzt.",
      }

export const sourceTag = (entry: ContactPoint) => contactPointOriginPresentation(entry)

export const importSyncTag = (entry: ContactPoint) => contactPointImportSyncPresentation(entry)

export const buildContactRows = (contacts: ContactPoint[]): CustomerContactRow[] =>
  contacts.map((entry) => ({
    id: entry.id,
    label: entry.label || contactKindLabel(entry.kind),
    kindLabel: contactKindLabel(entry.kind),
    channelsLabel: contactChannelTagsForContactPoint(entry)
      .map((tag) => tag.label)
      .join(" · "),
    value: contactValueLabel(entry),
    verifiedLabel: entry.is_verified ? "Ja" : "Nein",
    sourceLabel: [sourceTag(entry).label, importSyncTag(entry)?.label].filter(Boolean).join(" · "),
    contact: entry,
  }))
